interface TimeTravelCallback<State> {
  (state: State): void
}

/**
 * The API imitates windows.History with some differences:
 * 1. No related event. Imitate event 'onpopstate' through callback, there are 2 related method: `registerTimeTravelCallback` and `unregisterTimeTravelCallback`
 * 2. Strict type. The type of the parameter is restricted to generic parameter `State`
 * 3. Function signature changed. `pushState` only have the first parameter, which says, it would not try to change the url
 * 4. Throw error when out of range. If `go` to a non-existent state, an error will be thrown(windows.History ignores it)
 * 5. External public property/method. `currentIndex` must be exported to let user know whether undo/redo is valid or not, in fact
 *    we export two friendly property isBackValid/isForwardValid to help rather than `currentIndex`.
 */
export class TimeTravel<State> {
  private historyStates: State[] = [];

  private currentIndex = -1;

  get length() {
    return this.historyStates.length;
  }

  get isBackValid() {
    return this.currentIndex > 0;
  }

  get isForwardValid() {
    return this.currentIndex < this.length - 1;
  }

  private getHistoryState(historyIndex: number) {
    if (historyIndex < 0 || historyIndex >= this.length) {
      throw new Error('Could not achieve the state out of index!');
    }
    return this.historyStates[historyIndex];
  }

  private timeTravelCallbacks: TimeTravelCallback<State>[] = [];

  registerTimeTravelCallback(callback: TimeTravelCallback<State>) {
    this.timeTravelCallbacks.push(callback);
  }

  unregisterTimeTravelCallback(callback: TimeTravelCallback<State>) {
    this.timeTravelCallbacks = this.timeTravelCallbacks.filter(timeTravelCallback => timeTravelCallback !== callback);
  }

  private callTimeTravelCallBack(state: State) {
    this.timeTravelCallbacks.forEach((timeTravelCallback) => { timeTravelCallback(state) });
  }

  go(delta: number) {
    const targetHistoryIndex = this.currentIndex + delta;
    this.currentIndex = targetHistoryIndex;
    const targetHistoryState = this.getHistoryState(targetHistoryIndex);
    this.callTimeTravelCallBack(targetHistoryState);
  }

  back() {
    this.go(-1);
  }

  forward() {
    this.go(1);
  }

  pushState(state: State) {
    this.historyStates.splice(this.currentIndex + 1);
    this.historyStates.push(state);
    this.currentIndex += 1;
  }

  replaceState(state: State) {
    this.historyStates[this.currentIndex] = state;
  }
}
